import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import AppPageTitle from "../../components/AppPageTitle";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getAllSupportedTimezonesList } from "../../constants/businessTimezones";

function IconSettings() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="3" />
			<path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.01a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
		</svg>
	);
}

function IconSpark() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="m12 3 1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3Z" />
		</svg>
	);
}

function IconClock() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="10" />
			<polyline points="12 6 12 12 16 14" />
		</svg>
	);
}

function IconKey() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="7.5" cy="15.5" r="5.5" />
			<path d="m21 2-9.6 9.6" />
			<path d="m15.5 7.5 3 3" />
			<path d="M18 5l3 3" />
		</svg>
	);
}

function SummaryCard({ title, value, hint, icon, toneClassName }) {
	return (
		<div className="card h-full min-w-0">
			<div className="card-body flex items-start justify-between gap-4">
				<div className="min-w-0 flex-1">
					<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
						{title}
					</p>
					<p className="m-0 break-words text-2xl font-semibold text-default-800 dark:text-default-100">
						{value}
					</p>
					<p className="mt-2 mb-0 text-sm text-default-500 dark:text-default-400">
						{hint}
					</p>
				</div>
				<div className={`flex size-12 shrink-0 items-center justify-center rounded-2xl ${toneClassName}`}>
					{icon}
				</div>
			</div>
		</div>
	);
}

function SectionCard({ title, description, children }) {
	return (
		<section className="card min-w-0">
			<div className="card-header px-5 pt-5 pb-4">
				<h6 className="card-title m-0">{title}</h6>
				{description && (
					<p className="mt-1 mb-0 text-sm text-default-500 dark:text-default-400">
						{description}
					</p>
				)}
			</div>
			<div className="card-body space-y-4">{children}</div>
		</section>
	);
}

function ToggleSwitch({ checked, onChange, disabled = false }) {
	return (
		<button
			type="button"
			role="switch"
			aria-checked={checked}
			disabled={disabled}
			onClick={() => !disabled && onChange(!checked)}
			className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition ${
				checked ? "bg-primary" : "bg-default-300 dark:bg-default-700"
			} ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
		>
			<span
				className={`inline-block size-5 rounded-full bg-white shadow-sm transition ${
					checked ? "translate-x-6" : "translate-x-1"
				}`}
			/>
		</button>
	);
}

function SettingToggleCard({ title, description, checked, onChange, disabled = false }) {
	return (
		<div className="app-admin-setting-card rounded-2xl border border-default-200 bg-default-50 px-4 py-3 dark:border-default-700 dark:bg-default-900">
			<div className="app-admin-setting-card__row">
				<div className="min-w-0 flex-1">
					<p className="m-0 text-sm font-semibold text-default-900 dark:text-default-100">
						{title}
					</p>
					{description && (
						<p className="mt-1 mb-0 text-sm text-default-500 dark:text-default-400">
							{description}
						</p>
					)}
				</div>
				<ToggleSwitch checked={checked} onChange={onChange} disabled={disabled} />
			</div>
		</div>
	);
}

function SelectSettingCard({ title, description, name, value, options, onChange }) {
	return (
		<div className="app-admin-setting-card rounded-2xl border border-default-200 bg-default-50 px-4 py-3 dark:border-default-700 dark:bg-default-900">
			<div className="app-admin-setting-card__select-grid">
				<div className="min-w-0">
					<p className="m-0 text-sm font-semibold text-default-900 dark:text-default-100">
						{title}
					</p>
					{description && (
						<p className="mt-1 mb-0 text-sm text-default-500 dark:text-default-400">
							{description}
						</p>
					)}
				</div>
				<select
					className="form-select w-full min-w-0 max-w-full"
					name={name}
					value={value}
					onChange={event => onChange(name, event.target.value)}
				>
					{options.map(option => (
						<option key={`${name}-${option.value}`} value={option.value}>
							{option.label}
						</option>
					))}
				</select>
			</div>
		</div>
	);
}

function IntegrationCard({ id, title, description, children }) {
	return (
		<div
			id={id}
			className="app-admin-setting-card min-w-0 rounded-2xl border border-default-200 bg-default-50 px-4 py-4 dark:border-default-700 dark:bg-default-900"
		>
			<div className="mb-3">
				<p className="m-0 text-sm font-semibold text-default-900 dark:text-default-100">
					{title}
				</p>
				{description && (
					<p className="mt-1 mb-0 text-sm text-default-500 dark:text-default-400">
						{description}
					</p>
				)}
			</div>
			{children}
		</div>
	);
}

function normalizeBooleanSettingValue(value) {
	return value === "enabled";
}

function Settings() {
	const { user } = useContext(AuthContext);
	const [settings, setSettings] = useState([]);
	const [apiKey, setApiKey] = useState("");
	const [apiKeyFromCompany, setApiKeyFromCompany] = useState(false);
	const [regeneratingApiKey, setRegeneratingApiKey] = useState(false);
	const [savingHubToken, setSavingHubToken] = useState(false);

	const setLocalSettingValue = useCallback((key, value) => {
		setSettings(current => {
			const next = [...current];
			const index = next.findIndex(setting => setting.key === key);

			if (index >= 0) {
				next[index] = { ...next[index], value };
				return next;
			}

			return [...next, { key, value }];
		});
	}, []);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(Array.isArray(data) ? data : []);
			} catch (err) {
				toastError(err);
			}
		};

		fetchSession();
	}, []);

	useEffect(() => {
		const fetchApiKey = async () => {
			try {
				const { data } = await api.get("/company/api-key");
				setApiKey(data.apiKey ?? "");
				setApiKeyFromCompany(true);
				return;
			} catch (err) {
				if (err.response?.status === 403 || err.response?.status === 404) {
					setApiKeyFromCompany(false);
				} else {
					toastError(err);
				}
			}

			try {
				const { data } = await api.get("/settings");
				const list = Array.isArray(data) ? data : [];
				const apiTokenSetting = list.find(item => item.key === "userApiToken");
				setApiKey(apiTokenSetting?.value ?? "");
			} catch (err) {
				toastError(err);
			}
		};

		fetchApiKey();
	}, []);

	useEffect(() => {
		if (!user?.id) return undefined;

		const socket = openSocket({
			scope: "setings",
			userId: user.id,
			component: "Settings",
			companyId: user.companyId ?? "",
		});
		if (!socket) return undefined;

		socket.on("settings", data => {
			if (data.action === "update" && data.setting) {
				const sid = data.setting.companyId;
				if (sid != null && user.companyId != null && Number(sid) !== Number(user.companyId)) {
					return;
				}

				setSettings(prevState => {
					const next = [...prevState];
					const settingIndex = next.findIndex(setting => setting.key === data.setting.key);
					if (settingIndex !== -1) {
						next[settingIndex] = { ...next[settingIndex], ...data.setting };
					} else {
						next.push(data.setting);
					}
					return next;
				});

				if (data.setting.key === "userApiToken" && !apiKeyFromCompany) {
					setApiKey(data.setting.value ?? "");
				}
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [apiKeyFromCompany, user.id, user.companyId]);

	const getSettingValue = useCallback(
		key => {
			const setting = settings.find(item => item.key === key);
			return setting ? setting.value : "";
		},
		[settings]
	);

	const getBusinessTimezoneValue = useCallback(() => {
		const value = getSettingValue("businessTimezone");
		if (value === false || value == null) return "";
		return String(value);
	}, [getSettingValue]);

	const getBusinessTimezoneSelectValues = useCallback(() => {
		const saved = getBusinessTimezoneValue();
		const all = getAllSupportedTimezonesList();
		const set = new Set(all);

		if (saved && !set.has(saved)) {
			return [...all, saved].sort((a, b) => {
				if (a === "") return -1;
				if (b === "") return 1;
				return a.localeCompare(b);
			});
		}

		return all;
	}, [getBusinessTimezoneValue]);

	const updateSetting = async (key, value) => {
		try {
			await api.put(`/settings/${key}`, { value });
			setLocalSettingValue(key, value);
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleToggleSetting = (key, checked) => {
		updateSetting(key, checked ? "enabled" : "disabled");
	};

	const handleSelectSetting = (key, value) => {
		updateSetting(key, value);
	};

	const handleRegenerateApiKey = async () => {
		if (!window.confirm(i18n.t("settings.apiKey.regenerateConfirm"))) return;

		setRegeneratingApiKey(true);
		try {
			const { data } = await api.put("/company/api-key");
			setApiKey(data.apiKey ?? "");
			setApiKeyFromCompany(true);
			toast.success(i18n.t("settings.apiKey.regenerateSuccess"));
		} catch (err) {
			toastError(err);
		} finally {
			setRegeneratingApiKey(false);
		}
	};

	const handleCopyApiKey = async () => {
		if (!apiKey) return;
		try {
			await navigator.clipboard.writeText(apiKey);
			toast.success("Chave da API copiada.");
		} catch (err) {
			toastError(err);
		}
	};

	const handleSaveHubToken = async () => {
		setSavingHubToken(true);
		try {
			await api.put("/settings/notificameHub", {
				value: getSettingValue("notificameHub"),
			});
			toast.success("Token Hub atualizado com sucesso.");
		} catch (err) {
			toast.error(err?.response?.data?.error || "Erro ao atualizar");
		} finally {
			setSavingHubToken(false);
		}
	};

	const generalToggles = useMemo(
		() => [
			{
				key: "userCreation",
				title: i18n.t("settings.settings.userCreation.name"),
				description: "Permite liberar ou bloquear a criação de novos usuários pela empresa.",
			},
			{
				key: "CheckMsgIsGroup",
				title: i18n.t("settings.settings.CheckMsgIsGroup.name"),
				description: "Ignora mensagens vindas de grupos no atendimento.",
			},
			{
				key: "call",
				title: i18n.t("settings.settings.call.name"),
				description: i18n.t("settings.settings.call.note"),
			},
			{
				key: "sideMenu",
				title: i18n.t("settings.settings.sideMenu.name"),
				description: i18n.t("settings.settings.sideMenu.note"),
			},
			{
				key: "closeTicketApi",
				title: i18n.t("settings.settings.closeTicketApi.name"),
				description: i18n.t("settings.settings.closeTicketApi.note"),
			},
			{
				key: "darkMode",
				title: i18n.t("settings.settings.darkMode.name"),
				description: i18n.t("settings.settings.darkMode.note"),
			},
			// {
			// 	key: "StatusPerfil",
			// 	title: "Status do WhatsApp",
			// 	description:
			// 		"Quando ativado, captura stories do WhatsApp no backend e exibe a aba Status no chat. Quando desativado, a aba some e novos status deixam de ser salvos.",
			// },
			{
				key: "iniciarTicketAberto",
				title: "Iniciar conversa com ticket aberto",
				description: "Permite iniciar nova conversa mesmo quando já existe ticket em aberto.",
			},
			{
				key: "personalAssistantEnabled",
				title: "Assistente pessoal (IA)",
				description:
					"Habilita o assistente flutuante para usuários com permissão. Consome tokens da empresa e respeita a cota por usuário.",
			},
		],
		[]
	);

	const adminToggles = useMemo(
		() => [
			{
				key: "transcriptAudios",
				title: "Transcrever áudios",
				description: "Ativa a transcrição automática de áudios recebidos.",
			},
			{
				key: "legacyTicketsManager",
				title: "Cabeçalho de tickets legado",
				description: "Mantém o cabeçalho antigo do gerenciador de tickets.",
			},
			{
				key: "fecharTicket",
				title: "Fechar tickets ausentes",
				description: "Fecha tickets sem resposta há mais de 12 horas.",
			},
			{
				key: "TraducaoAutomaticaTexto",
				title: "Tradução automática de mensagens",
				description: "Traduz mensagens de texto ao fazer o envio da mensagem pra contatos com o idioma diferente de Português.",
			},
		],
		[]
	);

	const timeOptions = useMemo(
		() =>
			["10", "30", "60", "300", "1800", "3600", "7200", "21600", "43200", "86400", "604800", "1296000", "2592000"].map(
				value => ({
					value,
					label: i18n.t(`settings.settings.timeCreateNewTicket.options.${value}`),
				})
			),
		[]
	);

	const sortOptions = useMemo(
		() => [
			{ value: "desc", label: i18n.t("settings.settings.sortBy.options.desc") },
			{ value: "asc", label: i18n.t("settings.settings.sortBy.options.asc") },
		],
		[]
	);

	const timezoneOptions = useMemo(
		() =>
			getBusinessTimezoneSelectValues().map(value => ({
				value,
				label:
					value === ""
						? i18n.t("settings.settings.businessTimezone.options.zone_serverDefault")
						: value,
			})),
		[getBusinessTimezoneSelectValues]
	);

	const enabledCount = useMemo(
		() =>
			[...generalToggles, ...(user?.name === "admin" ? adminToggles : [])].filter(setting =>
				normalizeBooleanSettingValue(getSettingValue(setting.key))
			).length,
		[adminToggles, generalToggles, getSettingValue, user?.name]
	);

	const summary = useMemo(
		() => ({
			total: generalToggles.length + (user?.name === "admin" ? adminToggles.length : 0),
			enabled: enabledCount,
			timezone: getBusinessTimezoneValue() || "Servidor",
			hasApiKey: apiKey ? "Sim" : "Não",
		}),
		[adminToggles.length, apiKey, enabledCount, generalToggles.length, getBusinessTimezoneValue, user?.name]
	);

	return (
		<MainContainer>
			<div className="app-admin-page">
				<div className="app-admin-page__scroll">
					<AppPageTitle title={i18n.t("settings.title")} subtitle="Administração" />

					<div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
						<SummaryCard
							title="Configurações"
							value={summary.total}
							hint="Quantidade de opções visíveis nesta tela."
							icon={<IconSettings />}
							toneClassName="bg-primary/10 text-primary"
						/>
						<SummaryCard
							title="Ativadas"
							value={summary.enabled}
							hint="Opções booleanas atualmente habilitadas."
							icon={<IconSpark />}
							toneClassName="bg-success/10 text-success"
						/>
						<SummaryCard
							title="Fuso horário"
							value={summary.timezone}
							hint="Fuso usado para datas, horários e filtros do sistema."
							icon={<IconClock />}
							toneClassName="bg-info/10 text-info"
						/>
						<SummaryCard
							title="Chave da API"
							value={summary.hasApiKey}
							hint="Disponibilidade da chave para integrações."
							icon={<IconKey />}
							toneClassName="bg-warning/10 text-warning"
						/>
					</div>

					<SectionCard
						title="Comportamento geral"
						description="Configurações operacionais que afetam atendimento, tickets e interface."
					>
						<div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
							{generalToggles.map(setting => (
								<SettingToggleCard
									key={setting.key}
									title={setting.title}
									description={setting.description}
									checked={normalizeBooleanSettingValue(getSettingValue(setting.key))}
									onChange={checked => handleToggleSetting(setting.key, checked)}
								/>
							))}
						</div>
					</SectionCard>

						<SectionCard
							title="Configurações avançadas"
							description="Opções extras visíveis apenas para o usuário administrador."
						>
							<div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
								{adminToggles.map(setting => (
									<SettingToggleCard
										key={setting.key}
										title={setting.title}
										description={setting.description}
										checked={normalizeBooleanSettingValue(getSettingValue(setting.key))}
										onChange={checked => handleToggleSetting(setting.key, checked)}
									/>
								))}
							</div>
						</SectionCard>
				

					<SectionCard
						title="Tempo, ordem e fuso horário"
						description="Ajustes de reabertura de ticket, ordenação de listas e fuso horário da empresa."
					>
						<div className="space-y-4">
							<SelectSettingCard
								title={i18n.t("settings.settings.timeCreateNewTicket.name")}
								description={i18n.t("settings.settings.timeCreateNewTicket.note")}
								name="timeCreateNewTicket"
								value={String(getSettingValue("timeCreateNewTicket") || "60")}
								options={timeOptions}
								onChange={handleSelectSetting}
							/>

							<SelectSettingCard
								title={i18n.t("settings.settings.businessTimezone.name")}
								description={i18n.t("settings.settings.businessTimezone.note")}
								name="businessTimezone"
								value={getBusinessTimezoneValue()}
								options={timezoneOptions}
								onChange={handleSelectSetting}
							/>

							<SelectSettingCard
								title={i18n.t("settings.settings.sortBy.nameOpen")}
								description={i18n.t("settings.settings.sortBy.note")}
								name="sortByOpen"
								value={String(getSettingValue("sortByOpen") || "desc")}
								options={sortOptions}
								onChange={handleSelectSetting}
							/>

							<SelectSettingCard
								title={i18n.t("settings.settings.sortBy.namePending")}
								description={i18n.t("settings.settings.sortBy.note")}
								name="sortByPending"
								value={String(getSettingValue("sortByPending") || "desc")}
								options={sortOptions}
								onChange={handleSelectSetting}
							/>

							<SelectSettingCard
								title={i18n.t("settings.settings.sortBy.nameClosed")}
								description={i18n.t("settings.settings.sortBy.note")}
								name="sortByClosed"
								value={String(getSettingValue("sortByClosed") || "desc")}
								options={sortOptions}
								onChange={handleSelectSetting}
							/>
						</div>
					</SectionCard>

					<SectionCard
						title="Integrações"
						description="Chaves usadas por integrações externas e comunicação com o Hub."
					>
						<div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
							<IntegrationCard
								title="API Key"
								description="Chave de autenticação usada por integrações externas."
							>
								<div className="space-y-3">
									<input
										type="text"
										className="form-input w-full"
										value={apiKey}
										readOnly
										placeholder="Nenhuma API Key encontrada"
									/>
									<div className="flex flex-wrap gap-2">
										<button
											type="button"
											className="btn btn-sub-gray"
											onClick={handleCopyApiKey}
											disabled={!apiKey}
										>
											Copiar
										</button>
										{apiKeyFromCompany && (
											<button
												type="button"
												className="btn border border-danger/20 bg-danger/10 text-danger hover:bg-danger/15"
												onClick={handleRegenerateApiKey}
												disabled={regeneratingApiKey}
											>
												{regeneratingApiKey
													? "Regenerando..."
													: i18n.t("settings.apiKey.regenerate")}
											</button>
										)}
									</div>
									{apiKeyFromCompany && (
										<p className="m-0 text-xs text-default-500 dark:text-default-400">
											{i18n.t("settings.apiKey.regenerateWarning")}
										</p>
									)}
								</div>
							</IntegrationCard>

							<IntegrationCard
								id="hub-token"
								title="Token Hub"
								description="Token usado na integração com o NotificaMe Hub."
							>
								<div className="space-y-3">
									<input
										type="text"
										className="form-input w-full"
										value={String(getSettingValue("notificameHub") || "")}
										onChange={event =>
											setLocalSettingValue("notificameHub", event.target.value)
										}
										placeholder="Informe o token do Hub"
									/>
									<div className="flex flex-wrap gap-2">
										<button
											type="button"
											className="btn bg-primary text-white"
											onClick={handleSaveHubToken}
											disabled={savingHubToken}
										>
											{savingHubToken ? "Salvando..." : "Salvar"}
										</button>
									</div>
								</div>
							</IntegrationCard>

							<IntegrationCard
								id="assistant-web-search"
								title="Busca web do assistente (Serper)"
								description="Chave opcional da API Serper.dev para o assistente pessoal pesquisar no Google quando o usuário pedir."
							>
								<div className="space-y-3">
									<input
										type="password"
										className="form-input w-full"
										value={String(getSettingValue("assistantWebSearchKey") || "")}
										onChange={event =>
											setLocalSettingValue("assistantWebSearchKey", event.target.value)
										}
										placeholder="Chave Serper (opcional)"
										autoComplete="off"
									/>
									<div className="flex flex-wrap gap-2">
										<button
											type="button"
											className="btn bg-primary text-white"
											onClick={() =>
												updateSetting(
													"assistantWebSearchKey",
													String(getSettingValue("assistantWebSearchKey") || "")
												)
											}
										>
											Salvar
										</button>
									</div>
								</div>
							</IntegrationCard>
						</div>
					</SectionCard>
				</div>
			</div>
		</MainContainer>
	);
}

export default Settings;
